import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AUDIENCIAS, PLAZOS } from '../data/agenda';
import { EXPEDIENTES } from '../data/expedientes';
import { CLAVE_MATERIA, SUCURSALES } from '../data/catalogos';
import { ALMACENES_INICIALES, LETRAS_ESTANTE, nombreCaja } from '../data/almacenesConfig';
import { FLUJOS_INICIALES } from '../data/flujos';
import { TIPOS_DOCUMENTO_INICIALES } from '../data/tiposDocumento';
import { EVIDENCIAS_INICIALES } from '../data/evidencias';
import { documentosDe } from '../data/archivo';
import { ABOGADOS_INICIALES, abogadoPorId, sincronizarAbogados } from '../data/abogados';
import { CONEXIONES_GOOGLE_INICIALES, conexionVacia } from '../data/google';
import { ahoraISO, claveEvento, recalcularEventos } from '../utils/google';
import type { Abogado, Almacen, ConexionGoogle, EventoGoogle, OrigenEventoGoogle, Audiencia, Documento, EtapaFlujo, Expediente, FlujosPorMateria, Materia, Plazo, Rol, Sucursal, TipoDocumento } from '../types';
interface DatosContextValor {
  expedientes: Expediente[];
  plazos: Plazo[];
  audiencias: Audiencia[];
  flujos: FlujosPorMateria;
  rol: Rol;
  setRol: (rol: Rol) => void;
  sucursalActiva: string;
  setSucursalActiva: (id: string) => void;
  agregarExpediente: (exp: Expediente) => void;
  marcarPlazoCumplido: (id: string) => void;
  reasignarPlazo: (id: string, abogadoId: string) => void;
  generarLocalizador: (sucursalId: string, materia: Materia) => string;
  etapasDe: (materia: Materia) => EtapaFlujo[];
  agregarEtapa: (materia: Materia, etapa: EtapaFlujo) => void;
  actualizarEtapa: (materia: Materia, etapa: EtapaFlujo) => void;
  eliminarEtapa: (materia: Materia, etapaId: string) => void;
  duplicarEtapa: (materia: Materia, etapaId: string) => void;
  reordenarEtapas: (materia: Materia, desde: number, hacia: number) => void;
  duplicarFlujo: (origen: Materia, destino: Materia) => void;
  avanzarEtapa: (expedienteId: string, indice: number, plazo?: {
    concepto: string;
    fechaLimite: string;
    responsableId: string;
  }) => void;
  tiposDocumento: TipoDocumento[];
  tiposParaMateria: (materia: Materia) => TipoDocumento[];
  agregarTipoDocumento: (tipo: TipoDocumento) => void;
  actualizarTipoDocumento: (tipo: TipoDocumento) => void;
  alternarTipoDocumento: (id: string) => void;
  eliminarTipoDocumento: (id: string) => void;
  abogados: Abogado[];
  abogadoDe: (id: string) => Abogado | undefined;
  agregarAbogado: (abogado: Abogado) => void;
  actualizarAbogado: (abogado: Abogado) => void;
  alternarEstadoAbogado: (id: string) => void;
  sucursales: Sucursal[];
  agregarSucursal: (sucursal: Sucursal) => void;
  actualizarSucursal: (sucursal: Sucursal) => void;
  alternarSucursal: (id: string) => void;
  almacenes: Almacen[];
  agregarAlmacen: (almacen: Almacen) => void;
  actualizarAlmacen: (almacen: Almacen) => void;
  eliminarAlmacen: (id: string) => void;
  agregarPasillo: (almacenId: string) => void;
  agregarEstante: (almacenId: string, pasilloId: string) => void;
  agregarCaja: (almacenId: string, pasilloId: string, estanteId: string, capacidad: number) => void;
  conexionesGoogle: ConexionGoogle[];
  conexionGoogleDe: (abogadoId: string) => ConexionGoogle;
  conectarGoogle: (abogadoId: string, correoGoogle: string) => void;
  desconectarGoogle: (abogadoId: string) => void;
  actualizarConexionGoogle: (conexion: ConexionGoogle) => void;
  sincronizarGoogle: (abogadoId: string) => void;
  reenviarInvitacionGoogle: (abogadoId: string) => void;
  eventosGoogle: EventoGoogle[];
  eventoGoogleDe: (origen: OrigenEventoGoogle, origenId: string) => EventoGoogle | undefined;
  usuarioActualId: string;
  documentos: Documento[];
  documentosDeExpediente: (expedienteId: string) => Documento[];
  agregarDocumento: (documento: Documento) => void;
  reemplazarDocumento: (id: string, datos: {
    archivo: string;
    nota: string;
  }) => void;
  usuarioActual: string;
}
const USUARIO_ACTUAL_ID = 'ab-04';
const DatosContext = createContext<DatosContextValor | null>(null);
export function DatosProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [expedientes, setExpedientes] = useState<Expediente[]>(EXPEDIENTES);
  const [plazos, setPlazos] = useState<Plazo[]>(PLAZOS);
  const [audiencias] = useState<Audiencia[]>(AUDIENCIAS);
  const [flujos, setFlujos] = useState<FlujosPorMateria>(FLUJOS_INICIALES);
  const [rol, setRol] = useState<Rol>('Administrador');
  const [sucursalActiva, setSucursalActiva] = useState<string>('todas');
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>(TIPOS_DOCUMENTO_INICIALES);
  const [abogados, setAbogados] = useState<Abogado[]>(ABOGADOS_INICIALES);
  const [conexionesGoogle, setConexionesGoogle] = useState<ConexionGoogle[]>(CONEXIONES_GOOGLE_INICIALES);
  const [eventosGoogle, setEventosGoogle] = useState<EventoGoogle[]>(() => recalcularEventos(CONEXIONES_GOOGLE_INICIALES, AUDIENCIAS, PLAZOS, EXPEDIENTES, []));
  const [sucursales, setSucursales] = useState<Sucursal[]>(SUCURSALES);
  const [almacenes, setAlmacenes] = useState<Almacen[]>(ALMACENES_INICIALES);
  const [documentos, setDocumentos] = useState<Documento[]>(() => [...EVIDENCIAS_INICIALES, ...EXPEDIENTES.flatMap(e => documentosDe(e))]);
  const usuarioActual = abogadoPorId(USUARIO_ACTUAL_ID)?.nombre ?? 'Usuario del despacho';
  const agregarExpediente = useCallback((exp: Expediente) => {
    setExpedientes(prev => [exp, ...prev]);
  }, []);
  const marcarPlazoCumplido = useCallback((id: string) => {
    setPlazos(prev => prev.map(p => p.id === id ? {
      ...p,
      cumplido: true
    } : p));
  }, []);
  const reasignarPlazo = useCallback((id: string, abogadoId: string) => {
    setPlazos(prev => prev.map(p => p.id === id ? {
      ...p,
      responsableId: abogadoId
    } : p));
  }, []);
  const abogadoDe = useCallback((id: string) => abogados.find(a => a.id === id), [abogados]);
  const conexionGoogleDe = useCallback((abogadoId: string) => conexionesGoogle.find(c => c.abogadoId === abogadoId) ?? conexionVacia(abogadoId), [conexionesGoogle]);
  const guardarConexion = useCallback((conexion: ConexionGoogle) => {
    setConexionesGoogle(prev => prev.some(c => c.abogadoId === conexion.abogadoId) ? prev.map(c => c.abogadoId === conexion.abogadoId ? conexion : c) : [...prev, conexion]);
  }, []);
  const conectarGoogle = useCallback((abogadoId: string, correoGoogle: string) => {
    const base = conexionGoogleDe(abogadoId);
    guardarConexion({
      ...base,
      estado: 'conectado',
      correoGoogle,
      ultimaSync: ahoraISO(),
      mensajeError: undefined
    });
  }, [conexionGoogleDe, guardarConexion]);
  const desconectarGoogle = useCallback((abogadoId: string) => {
    const base = conexionGoogleDe(abogadoId);
    guardarConexion({
      ...base,
      estado: 'no_conectado',
      correoGoogle: undefined,
      ultimaSync: undefined,
      mensajeError: undefined
    });
  }, [conexionGoogleDe, guardarConexion]);
  const sincronizarGoogle = useCallback((abogadoId: string) => {
    const base = conexionGoogleDe(abogadoId);
    guardarConexion({
      ...base,
      ultimaSync: ahoraISO()
    });
  }, [conexionGoogleDe, guardarConexion]);
  const reenviarInvitacionGoogle = useCallback((abogadoId: string) => {
    const base = conexionGoogleDe(abogadoId);
    guardarConexion({
      ...base,
      invitacionEnviada: ahoraISO()
    });
  }, [conexionGoogleDe, guardarConexion]);
  const eventoGoogleDe = useCallback((origen: OrigenEventoGoogle, origenId: string) => eventosGoogle.find(e => claveEvento(e.origen, e.origenId) === claveEvento(origen, origenId)), [eventosGoogle]);

  /**
   * Sincronización del sistema hacia Google: cada cambio en agenda, términos o
   * preferencias vuelve a crear, actualizar o eliminar los eventos espejo.
   */
  useEffect(() => {
    setEventosGoogle(prev => recalcularEventos(conexionesGoogle, audiencias, plazos, expedientes, prev));
  }, [conexionesGoogle, audiencias, plazos, expedientes]);
  const agregarAbogado = useCallback((abogado: Abogado) => {
    setAbogados(prev => {
      const lista = [...prev, abogado];
      sincronizarAbogados(lista);
      return lista;
    });
  }, []);
  const actualizarAbogado = useCallback((abogado: Abogado) => {
    setAbogados(prev => {
      const lista = prev.map(a => a.id === abogado.id ? abogado : a);
      sincronizarAbogados(lista);
      return lista;
    });
  }, []);
  const alternarEstadoAbogado = useCallback((id: string) => {
    setAbogados(prev => {
      const lista = prev.map(a => a.id === id ? {
        ...a,
        activo: !a.activo
      } : a);
      sincronizarAbogados(lista);
      return lista;
    });
  }, []);
  const agregarSucursal = useCallback((sucursal: Sucursal) => {
    setSucursales(prev => [...prev, sucursal]);
  }, []);
  const actualizarSucursal = useCallback((sucursal: Sucursal) => {
    setSucursales(prev => prev.map(s => s.id === sucursal.id ? sucursal : s));
  }, []);
  const alternarSucursal = useCallback((id: string) => {
    setSucursales(prev => prev.map(s => s.id === id ? {
      ...s,
      activa: !s.activa
    } : s));
  }, []);
  const agregarAlmacen = useCallback((almacen: Almacen) => {
    setAlmacenes(prev => [...prev, almacen]);
  }, []);
  const actualizarAlmacen = useCallback((almacen: Almacen) => {
    setAlmacenes(prev => prev.map(a => a.id === almacen.id ? almacen : a));
  }, []);
  const eliminarAlmacen = useCallback((id: string) => {
    setAlmacenes(prev => prev.filter(a => a.id !== id));
  }, []);
  const agregarPasillo = useCallback((almacenId: string) => {
    setAlmacenes(prev => prev.map(a => {
      if (a.id !== almacenId) return a;
      const numero = a.pasillos.length + 1;
      return {
        ...a,
        pasillos: [...a.pasillos, {
          id: `${a.id}-p${Date.now()}`,
          nombre: `Pasillo ${numero}`,
          estantes: []
        }]
      };
    }));
  }, []);
  const agregarEstante = useCallback((almacenId: string, pasilloId: string) => {
    setAlmacenes(prev => prev.map(a => {
      if (a.id !== almacenId) return a;
      return {
        ...a,
        pasillos: a.pasillos.map(p => {
          if (p.id !== pasilloId) return p;
          const letra = LETRAS_ESTANTE[p.estantes.length] ?? String(p.estantes.length + 1);
          return {
            ...p,
            estantes: [...p.estantes, {
              id: `${p.id}-e${Date.now()}`,
              nombre: `Estante ${letra}`,
              cajas: []
            }]
          };
        })
      };
    }));
  }, []);
  const agregarCaja = useCallback((almacenId: string, pasilloId: string, estanteId: string, capacidad: number) => {
    setAlmacenes(prev => prev.map(a => {
      if (a.id !== almacenId) return a;
      return {
        ...a,
        pasillos: a.pasillos.map(p => {
          if (p.id !== pasilloId) return p;
          return {
            ...p,
            estantes: p.estantes.map(e => {
              if (e.id !== estanteId) return e;
              return {
                ...e,
                cajas: [...e.cajas, {
                  id: `${e.id}-c${Date.now()}`,
                  nombre: nombreCaja(e.cajas.length + 1),
                  capacidad
                }]
              };
            })
          };
        })
      };
    }));
  }, []);
  const generarLocalizador = useCallback((sucursalId: string, materia: Materia) => {
    const sucursal = sucursales.find(s => s.id === sucursalId);
    if (!sucursal || !materia) return '';
    const anio = new Date().getFullYear();
    const prefijo = `${sucursal.clave}-${CLAVE_MATERIA[materia]}-${anio}-`;
    const consecutivos = expedientes.filter(e => e.localizador.startsWith(prefijo)).map(e => Number(e.localizador.split('-')[3])).filter(n => !Number.isNaN(n));
    const siguiente = (consecutivos.length ? Math.max(...consecutivos) : 120) + 1;
    return `${prefijo}${String(siguiente).padStart(4, '0')}`;
  }, [expedientes, sucursales]);
  const etapasDe = useCallback((materia: Materia) => flujos[materia] ?? [], [flujos]);
  const agregarEtapa = useCallback((materia: Materia, etapa: EtapaFlujo) => {
    setFlujos(prev => ({
      ...prev,
      [materia]: [...prev[materia], etapa]
    }));
  }, []);
  const actualizarEtapa = useCallback((materia: Materia, etapa: EtapaFlujo) => {
    setFlujos(prev => ({
      ...prev,
      [materia]: prev[materia].map(e => e.id === etapa.id ? etapa : e)
    }));
  }, []);
  const eliminarEtapa = useCallback((materia: Materia, etapaId: string) => {
    setFlujos(prev => ({
      ...prev,
      [materia]: prev[materia].filter(e => e.id !== etapaId)
    }));
  }, []);
  const duplicarEtapa = useCallback((materia: Materia, etapaId: string) => {
    setFlujos(prev => {
      const lista = prev[materia];
      const i = lista.findIndex(e => e.id === etapaId);
      if (i === -1) return prev;
      const copia: EtapaFlujo = {
        ...lista[i],
        id: `et-${Date.now()}`,
        nombre: `${lista[i].nombre} (copia)`
      };
      return {
        ...prev,
        [materia]: [...lista.slice(0, i + 1), copia, ...lista.slice(i + 1)]
      };
    });
  }, []);
  const reordenarEtapas = useCallback((materia: Materia, desde: number, hacia: number) => {
    setFlujos(prev => {
      const lista = [...prev[materia]];
      if (desde === hacia || desde < 0 || hacia < 0 || desde >= lista.length) return prev;
      const [movida] = lista.splice(desde, 1);
      lista.splice(Math.min(hacia, lista.length), 0, movida);
      return {
        ...prev,
        [materia]: lista
      };
    });
  }, []);
  const duplicarFlujo = useCallback((origen: Materia, destino: Materia) => {
    setFlujos(prev => ({
      ...prev,
      [destino]: prev[origen].map((e, i) => ({
        ...e,
        id: `et-${Date.now()}-${i}`
      }))
    }));
  }, []);
  const avanzarEtapa = useCallback((expedienteId: string, indice: number, plazo?: {
    concepto: string;
    fechaLimite: string;
    responsableId: string;
  }) => {
    setExpedientes(prev => prev.map(e => e.id === expedienteId ? {
      ...e,
      etapaActual: indice
    } : e));
    if (plazo) {
      setPlazos(prev => [...prev, {
        id: `pl-${Date.now()}`,
        expedienteId,
        concepto: plazo.concepto,
        tipo: 'Término judicial',
        fechaLimite: plazo.fechaLimite,
        responsableId: plazo.responsableId,
        cumplido: false
      }]);
    }
  }, []);
  const tiposParaMateria = useCallback((materia: Materia) => tiposDocumento.filter(t => t.activo && (t.aplicaTodas || t.materias.includes(materia))), [tiposDocumento]);
  const agregarTipoDocumento = useCallback((tipo: TipoDocumento) => {
    setTiposDocumento(prev => [...prev, tipo]);
  }, []);
  const actualizarTipoDocumento = useCallback((tipo: TipoDocumento) => {
    setTiposDocumento(prev => prev.map(t => t.id === tipo.id ? tipo : t));
  }, []);
  const alternarTipoDocumento = useCallback((id: string) => {
    setTiposDocumento(prev => prev.map(t => t.id === id ? {
      ...t,
      activo: !t.activo
    } : t));
  }, []);
  const eliminarTipoDocumento = useCallback((id: string) => {
    setTiposDocumento(prev => prev.filter(t => t.id !== id));
  }, []);
  const agregarDocumento = useCallback((documento: Documento) => {
    setDocumentos(prev => [documento, ...prev]);
  }, []);
  const documentosDeExpediente = useCallback((expedienteId: string) => documentos.filter(d => d.expedienteId === expedienteId).sort((a, b) => a.fecha < b.fecha ? 1 : -1), [documentos]);
  const reemplazarDocumento = useCallback((id: string, datos: {
    archivo: string;
    nota: string;
  }) => {
    setDocumentos(prev => prev.map(d => {
      if (d.id !== id) return d;
      const versiones = d.versiones ?? [];
      const ahora = new Date();
      const fechaHora = `${ahora.toISOString().slice(0, 10)} ${ahora.toTimeString().slice(0, 5)}`;
      return {
        ...d,
        archivo: datos.archivo,
        subidoPor: usuarioActual,
        subidoEn: fechaHora,
        versiones: [...versiones, {
          version: versiones.length + 1,
          archivo: datos.archivo,
          usuario: usuarioActual,
          fechaHora,
          nota: datos.nota
        }]
      };
    }));
  }, [usuarioActual]);
  const valor = useMemo(() => ({
    expedientes,
    plazos,
    audiencias,
    flujos,
    tiposDocumento,
    tiposParaMateria,
    agregarTipoDocumento,
    actualizarTipoDocumento,
    alternarTipoDocumento,
    eliminarTipoDocumento,
    abogados,
    abogadoDe,
    agregarAbogado,
    actualizarAbogado,
    alternarEstadoAbogado,
    conexionesGoogle,
    conexionGoogleDe,
    conectarGoogle,
    desconectarGoogle,
    actualizarConexionGoogle: guardarConexion,
    sincronizarGoogle,
    reenviarInvitacionGoogle,
    eventosGoogle,
    eventoGoogleDe,
    usuarioActualId: USUARIO_ACTUAL_ID,
    sucursales,
    agregarSucursal,
    actualizarSucursal,
    alternarSucursal,
    almacenes,
    agregarAlmacen,
    actualizarAlmacen,
    eliminarAlmacen,
    agregarPasillo,
    agregarEstante,
    agregarCaja,
    documentos,
    documentosDeExpediente,
    agregarDocumento,
    reemplazarDocumento,
    usuarioActual,
    rol,
    setRol,
    sucursalActiva,
    setSucursalActiva,
    agregarExpediente,
    marcarPlazoCumplido,
    reasignarPlazo,
    generarLocalizador,
    etapasDe,
    agregarEtapa,
    actualizarEtapa,
    eliminarEtapa,
    duplicarEtapa,
    reordenarEtapas,
    duplicarFlujo,
    avanzarEtapa
  }), [expedientes, plazos, audiencias, flujos, tiposDocumento, tiposParaMateria, agregarTipoDocumento, actualizarTipoDocumento, alternarTipoDocumento, eliminarTipoDocumento, abogados, abogadoDe, agregarAbogado, actualizarAbogado, alternarEstadoAbogado, conexionesGoogle, conexionGoogleDe, conectarGoogle, desconectarGoogle, guardarConexion, sincronizarGoogle, reenviarInvitacionGoogle, eventosGoogle, eventoGoogleDe, sucursales, agregarSucursal, actualizarSucursal, alternarSucursal, almacenes, agregarAlmacen, actualizarAlmacen, eliminarAlmacen, agregarPasillo, agregarEstante, agregarCaja, documentos, documentosDeExpediente, agregarDocumento, reemplazarDocumento, usuarioActual, rol, sucursalActiva, agregarExpediente, marcarPlazoCumplido, reasignarPlazo, generarLocalizador, etapasDe, agregarEtapa, actualizarEtapa, eliminarEtapa, duplicarEtapa, reordenarEtapas, duplicarFlujo, avanzarEtapa]);
  return <DatosContext.Provider value={valor} data-id="element-930" data-mp="1n76ii8qiawt0">{children}</DatosContext.Provider>;
}
export function useDatos(): DatosContextValor {
  const ctx = useContext(DatosContext);
  if (!ctx) throw new Error('useDatos debe usarse dentro de DatosProvider');
  return ctx;
}